/**
 * FormulaValueLabel - the single component that owns formula schema
 * resolution for read-only formula rendering.
 *
 * Every other formula read surface used to compose its own
 * `useFormulaTargetSchema` call and thread the result through as a
 * `formulaTargetSchema` prop — and any surface that forgot rendered raw
 * internal identifiers instead of resolved chips (e.g. the timeline). This
 * component owns that resolution so callers only need to supply the
 * formula's target entity type name (or nothing at all, in which case the
 * formula degrades to plain tokenized text).
 */

import { useFormulaTargetSchema } from '../../../hooks/useFormulaTargetSchema';
import { FormulaDisplay } from '../FormulaDisplay';

export interface FormulaValueLabelProps {
  formula: unknown;
  /** The entity type the formula runs over. */
  targetEntityTypeName?: string;
  orgId?: number;
  truncate?: boolean;
  className?: string;
  /** Truncate the formula source to this many characters with "…". */
  maxLength?: number;
}

export function FormulaValueLabel({
  formula,
  targetEntityTypeName,
  orgId,
  truncate,
  className,
  maxLength,
}: FormulaValueLabelProps) {
  // Pass a disabled org id (0) when there is no target entity type, so the hook's
  // association-type catalog fetch (guarded only on orgId) never starts for a
  // formula that has no target and degrades to plain tokenized text anyway.
  const schema = useFormulaTargetSchema(
    targetEntityTypeName ? (orgId ?? 0) : 0,
    targetEntityTypeName ?? ''
  );

  return (
    <FormulaDisplay
      formula={formula}
      schema={schema}
      orgId={orgId}
      truncate={truncate}
      className={className}
      maxLength={maxLength}
    />
  );
}
